import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select, Option } from '@/components/ui/Select';
import { Toast } from '@/components/ui/Toast';
import {
    Container,
    Form,
    FormGroup,
    Label,
    DeleteButton
} from '../styles/AddScenarioPage.styles';
import { Scenario } from '@/types/assistants';
import { scenarioApi } from '@/api/scenarios';
import { ToastState } from '@/components/auth/types/register.types';
import { useAppSelector } from '@/hooks/redux';
import {Flex, FlexJustifyBetween} from "@/components/ui/FlexBox.ts";
import { styled } from 'styled-components';

const CheckboxLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    font-size: 14px;
    font-weight: 700;
`;

const StyledCheckbox = styled.input`
    width: 20px;
    height: 20px;
    border-radius: 4px;
    appearance: none;
    border: 1px solid #ddd;
    outline: none;
    cursor: pointer;
    position: relative;

    &:checked {
        background-color: rgb(77, 105, 156);
        border-color: rgb(77, 105, 156);
    }

    &:checked::after {
        content: '✓';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: white;
        font-size: 12px;
    }
`;

interface FormState {
    name: string;
    description: string;
    script_id: string;
    assistant_id: number | null;
    is_default: boolean;
}

export const CreateNewScenarioTab = ({ scenario }: {scenario?: Scenario}) => {
    const { register, handleSubmit } = useForm();
    const navigate = useNavigate();
    const { id, scenarioId } = useParams();
    const assistant = useAppSelector(state => state.assistant.assistant);

    const [formState, setFormState] = useState<FormState>({
        name: '',
        description: '',
        script_id: '',
        assistant_id: null,
        is_default: false
    });
    const [toast, setToast] = useState<ToastState>({
        show: false,
        message: '',
        type: 'success'
    });

    useEffect(() => {
        if(scenario) {
            setFormState({
                name: scenario.Name,
                description: scenario.Description,
                script_id: scenario.ScriptID,
                assistant_id: scenario.AssistantID,
                is_default: scenario.IsDefault
            });
        }
    }, [scenario]);


    const exportScenario = async () => {
        if(scenarioId){
            const response = await scenarioApi.exportScenario(scenarioId);
            const yamlContent = response.data.yaml_content; // Строка с содержимым YAML

            const file = new Blob([yamlContent], { type: 'application/x-yaml' });

            const fileURL = window.URL.createObjectURL(file);

            const link = document.createElement('a');
            link.href = fileURL;
            link.setAttribute('download', 'scenario.yaml'); // Имя файла
            link.click();

            window.URL.revokeObjectURL(fileURL);
        }
    }


    const onSubmit = async (data: any) => {
        const scenarioData = {
            name: data.name || formState.name,
            description: data.description || formState.description,
            assistant_id: assistant?.ID || formState.assistant_id,
            is_default: data.is_default !== undefined ? data.is_default : formState.is_default,
        };
        try {
            if (scenarioId) {
                await scenarioApi.update({ ...scenarioData, id: parseInt(scenarioId) });
                setToast({
                    show: true,
                    message: 'Сценарий успешно обновлен',
                    type: 'success'
                });
            } else {
                await scenarioApi.create(scenarioData);
                setToast({
                    show: true,
                    message: 'Сценарий успешно добавлен',
                    type: 'success'
                });
            }
            navigate(`/dashboard/assistants/${assistant?.ID}/scenarios`);
        } catch (error) {
            console.error(error);
            setToast({
                show: true,
                message: 'Ошибка при сохранении сценария',
                type: 'error'
            });
        }
    };

    const handleDeleteScenario = async () => {
        if (!scenarioId) return;
        try {
            await scenarioApi.delete(parseInt(scenarioId));
            setToast({
                show: true,
                message: 'Сценарий успешно удален',
                type: 'success'
            });
            navigate(`/dashboard/assistants/${id}/scenarios`);
        } catch (error) {
            setToast({
                show: true,
                message: 'Ошибка при удалении сценария',
                type: 'error'
            });
        }
    };
    return (
        <Container>
            {toast.show && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(prev => ({...prev, show: false}))}
                />
            )}
            <Form onSubmit={handleSubmit(onSubmit)}>
                <FormGroup>
                    <Label>Ассистент</Label>
                    <Select width='410px' disabled>
                        <Option value="">{assistant?.Name}</Option>
                    </Select>
                </FormGroup>

                <FormGroup>
                    <Label>Имя сценария</Label>
                    <Input
                        {...register('name')}
                        defaultValue={formState.name}
                        placeholder="Имя моего сценария"
                        width='410px'
                    />
                </FormGroup>
                <FormGroup>
                    <Label>Описание сценария</Label>
                    <Input
                        {...register('description')}
                        defaultValue={formState.description}
                        placeholder="Описание моего сценария"
                        width='410px'
                    />
                </FormGroup>
                <FormGroup>
                    <Label></Label>
                    <CheckboxLabel>
                        <StyledCheckbox
                            type="checkbox"
                            {...register('is_default')}
                            defaultChecked={formState.is_default}
                        />
                        По умолчанию
                    </CheckboxLabel>
                </FormGroup>
                <Flex style={{ justifyContent: 'end'}}>
                    <FlexJustifyBetween style={{width: '410px'}}>
                        <Button variant="primary" type="submit">
                            {scenarioId ? 'Сохранить' : 'Добавить'}
                        </Button>
                        {scenarioId && (
                            <Button variant="link" type="button" onClick={exportScenario}>
                                Экспорт
                            </Button>
                        )}
                        {scenarioId && (
                            <DeleteButton
                                type="button"
                                onClick={handleDeleteScenario}
                            >
                                Удалить сценарий
                            </DeleteButton>
                        )}
                    </FlexJustifyBetween>
                </Flex>
            </Form>
        </Container>
    );
};
